import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export interface Role {
  role_key: string;
  role_name: string;
  role_description?: string;
}

export interface GroupRoleMap {
  _id?: string;
  group_key: string;
  roles: Role[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateGroupRoleMapDto {
  group_key: string;
  roles: Role[];
}

export interface UpdateGroupRoleMapDto {
  new_group_key?: string;
  roles?: Role[];
}

class GroupRoleService {
  /**
   * Get all available roles (dropdown - minimal data)
   */
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ROLES.DROPDOWN);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  /**
   * Get group role mapping by group key
   */
  async getGroupRoleByGroupKey(groupKey: string): Promise<GroupRoleMap | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUP_ROLE_MAP.GET_BY_KEY(groupKey));
      return response.data?.data || response.data || null;
    } catch (error) {
      // If it's a 404, return null (no mapping exists yet)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching group role mapping:', error);
      throw error;
    }
  }

  /**
   * Create a new group role mapping
   */
  async createGroupRoleMap(data: CreateGroupRoleMapDto): Promise<GroupRoleMap> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUP_ROLE_MAP.CREATE, data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error creating group role mapping:', error);
      throw error;
    }
  }

  /**
   * Update group role mapping by group key
   */
  async updateGroupRoleByGroupKey(groupKey: string, data: UpdateGroupRoleMapDto): Promise<GroupRoleMap> {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.GROUP_ROLE_MAP.UPDATE(groupKey), data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating group role mapping:', error);
      throw error;
    }
  }

  /**
   * Delete group role mapping by group key
   */
  async deleteGroupRoleByGroupKey(groupKey: string): Promise<string> {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.GROUP_ROLE_MAP.DELETE(groupKey));
      return response.data?.data || groupKey;
    } catch (error) {
      console.error('Error deleting group role mapping:', error);
      throw error;
    }
  }

  /**
   * Get or create group role mapping - helper method
   * This will try to get existing mapping, or create one if it doesn't exist
   */
  async getOrCreateGroupRoleMapping(groupKey: string, initialRoles: Role[] = []): Promise<GroupRoleMap> {
    try {
      // First try to get existing mapping
      const existing = await this.getGroupRoleByGroupKey(groupKey);
      if (existing) {
        return existing;
      }

      // If no mapping exists, create one
      return await this.createGroupRoleMap({
        group_key: groupKey,
        roles: initialRoles
      });
    } catch (error) {
      console.error('Error getting or creating group role mapping:', error);
      throw error;
    }
  }

  /**
   * Update group roles - combines create/update logic
   * This will create a mapping if it doesn't exist, or update if it does
   */
  async saveGroupRoles(groupKey: string, roles: Role[]): Promise<GroupRoleMap> {
    try {
      const existing = await this.getGroupRoleByGroupKey(groupKey);

      if (existing) {
        // Update existing mapping
        return await this.updateGroupRoleByGroupKey(groupKey, { roles });
      } else {
        // Create new mapping
        return await this.createGroupRoleMap({
          group_key: groupKey,
          roles
        });
      }
    } catch (error) {
      console.error('Error saving group roles:', error);
      throw error;
    }
  }

  /**
   * Upsert group roles using the new backend upsert endpoint
   * This is the preferred method - uses a single API call instead of get + create/update
   */
  async upsertGroupRoles(groupKey: string, roles: Role[]): Promise<GroupRoleMap> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUP_ROLE_MAP.UPSERT, {
        group_key: groupKey,
        roles
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error upserting group roles:', error);
      throw error;
    }
  }
}

export const groupRoleService = new GroupRoleService();